import { canonicalSync, syncId } from "@classroom/domain";
import type { LocalSnapshot, StoredRecord } from "../localStore/repository";
import { COLLECTION_TABLES, type Collection } from "../localStore/schema";

export const SYNC_SQL_AUDIT_COLLECTIONS = [
  "attendanceRecords",
  "classes",
  "dailyRecords",
  "homeroomHomeworkRecords",
  "homeroomRecords",
  "homeworkAssignments",
  "homeworkRecords",
  "seatingCharts",
  "studentRoutineItems",
  "students",
] as const satisfies readonly Collection[];

type AuditCollection = (typeof SYNC_SQL_AUDIT_COLLECTIONS)[number];
export type SyncAuditScope = { datasetId: string; generation: string; schemaVersion: number };
const included = new Set<string>(SYNC_SQL_AUDIT_COLLECTIONS);
const excluded = new Set<string>([
  "calendarEvents",
  "personalNotes",
  "personalNotePages",
  "quizzes",
  "quizSessions",
  "quizResponses",
  "quizAttempts",
]);
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const literal = (value: string) => {
  if (value.includes("\0")) throw new Error("sync-audit-format");
  return `'${value.replaceAll("'", "''")}'`;
};

// Match the top-level metadata omitted by journalChanges(). Never recursively
// strip similarly named fields: nested business content must remain detectable.
function businessRecord(input: StoredRecord) {
  const {
    workspaceId: _workspace,
    revision: _revision,
    lastOperationId: _operation,
    updatedAt: _time,
    ...value
  } = input;
  return value;
}

export function syncAuditSql(snapshot: LocalSnapshot, scope: SyncAuditScope) {
  if (
    !syncId(scope.datasetId) ||
    !syncId(scope.generation) ||
    !Number.isSafeInteger(scope.schemaVersion) ||
    scope.schemaVersion < 1
  )
    throw new Error("sync-audit-scope");
  const records = snapshot.records.filter((row) => {
    if (included.has(row.collection)) return true;
    if (excluded.has(row.collection)) return false;
    throw new Error("sync-audit-collection");
  });
  const identities = new Set<string>();
  for (const row of records) {
    if (!row.recordId || row.data.id !== row.recordId) throw new Error("sync-audit-record-id");
    const identity = canonicalSync([row.collection, row.recordId]);
    if (identities.has(identity)) throw new Error("sync-audit-duplicate-record");
    identities.add(identity);
  }
  records.sort((a, b) => compare(a.collection, b.collection) || compare(a.recordId, b.recordId));
  const { noteNotebooks: _books, ...workspace } = businessRecord(snapshot.workspace);
  const counts = Object.fromEntries(SYNC_SQL_AUDIT_COLLECTIONS.map((name) => [name, 0])) as Record<
    AuditCollection,
    number
  >;
  const lines = [
    "-- classroom-sync-sql-audit-v1",
    "-- Logical classroom comparison only; not an application restore file.",
    "-- Scope excludes calendars, notes and quiz archives. UTF-8, LF, no BOM.",
    "BEGIN TRANSACTION;",
    "CREATE TABLE audit_scope (dataset_id TEXT NOT NULL,generation TEXT NOT NULL,schema_version INTEGER NOT NULL);",
    `INSERT INTO audit_scope VALUES (${literal(scope.datasetId)},${literal(scope.generation)},${scope.schemaVersion});`,
    "CREATE TABLE workspace_state (workspace_json TEXT NOT NULL,hidden_tools_json TEXT NOT NULL);",
    `INSERT INTO workspace_state VALUES (${literal(canonicalSync(workspace))},${literal(canonicalSync(snapshot.hiddenTools))});`,
  ];
  for (const collection of SYNC_SQL_AUDIT_COLLECTIONS) {
    const table = COLLECTION_TABLES[collection];
    lines.push(`CREATE TABLE ${table} (record_id TEXT PRIMARY KEY,data_json TEXT NOT NULL);`);
    for (const row of records.filter((item) => item.collection === collection)) {
      counts[collection]++;
      lines.push(
        `INSERT INTO ${table} VALUES (${literal(row.recordId)},${literal(canonicalSync(businessRecord(row.data)))});`,
      );
    }
  }
  lines.push("COMMIT;", "");
  return { sql: lines.join("\n"), counts, totalRecords: records.length };
}
